import { useState, useEffect } from "react";
import { partnersWSManager } from "../services/partnersWSManager";

/**
 * @param {string}  symbol      - e.g. "ADVAIT"
 * @param {number}  entryPrice  - price at which trade was entered (optional)
 * @param {number}  quantity    - number of shares (optional)
 * @param {"BUY"|"SELL"} tradeType
 * @param {boolean} enabled     - pause without unmounting
 *
 * @returns {{ ltp, pnl, pnlPercent, tickData, isConnected }}
 *   pnl / pnlPercent are null if entryPrice or quantity is not provided
 */
const usePartnersWS = ({
  symbol,
  entryPrice = null,
  quantity = null,
  tradeType = "BUY",
  enabled = true,
} = {}) => {
  const [tickData, setTickData] = useState(null);
  const [ltp, setLtp] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [pnlPercent, setPnlPercent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // Track live connection status
  useEffect(() => {
    const unsubStatus = partnersWSManager.subscribeStatus(setIsConnected);
    return unsubStatus;
  }, []);

  // Reset stale values whenever the symbol changes — otherwise the previous
  // symbol's last tick keeps showing until a fresh tick for the new symbol arrives
  useEffect(() => {
    setTickData(null);
    setLtp(null);
    setPnl(null);
    setPnlPercent(null);
  }, [symbol]);

  // Subscribe to symbol ticks
  useEffect(() => {
    if (!enabled || !symbol) return;

    const unsubscribe = partnersWSManager.subscribe(symbol, (data) => {
      const liveLtp = parseFloat(data.ltp);
      setTickData(data);
      setLtp(liveLtp);

      if (entryPrice != null && quantity != null) {
        const ep = parseFloat(entryPrice);
        const qty = parseFloat(quantity);
        const rawPnl =
          tradeType === "SELL"
            ? (ep - liveLtp) * qty
            : (liveLtp - ep) * qty;
        const rawPct = (rawPnl / (ep * qty)) * 100;
        setPnl(parseFloat(rawPnl.toFixed(2)));
        setPnlPercent(parseFloat(rawPct.toFixed(2)));
      }
    });

    return unsubscribe;
  }, [symbol, entryPrice, quantity, tradeType, enabled]);

  return { ltp, pnl, pnlPercent, tickData, isConnected };
};

export default usePartnersWS;
